/**
 * 学科分层的共享渲染与常量(Jarod 2026-08-27)。
 *
 * 三档固定:advanced / intermediate / basic —— 与课堂诊断的
 * challenge / standard / foundation 一一对应(那是单次课的临时分组,
 * 这里是长期固定分层),配色刻意沿用同一套,让老师一眼认出是同一
 * 心智模型。学科可只启用其中两档甚至不分层。
 */
import { useTranslation } from "react-i18next";

export const LEVEL_KEYS = ["advanced", "intermediate", "basic"] as const;
export type LevelKey = (typeof LEVEL_KEYS)[number];

const LEVEL_STYLE: Record<LevelKey, string> = {
  advanced: "bg-amber-50 text-amber-700 border-amber-200",
  intermediate: "bg-sky-50 text-sky-700 border-sky-200",
  basic: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

/** 层级徽标。level 为空时渲染 null —— 未分层的学生不占位。 */
export function LevelBadge({
  level,
  subjectName,
  className = "",
}: {
  level?: string | null;
  subjectName?: string;
  className?: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (!level || !LEVEL_KEYS.includes(level as LevelKey)) return null;
  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] leading-none ${LEVEL_STYLE[level as LevelKey]} ${className}`}
      title={subjectName ? `${subjectName} · ${t(`levels.${level}`)}` : undefined}
    >
      {subjectName ? `${subjectName}·` : ""}
      {t(`levels.${level}`)}
    </span>
  );
}

/** 把 {subject_id: level} 渲染成一排徽标(学生名册/详情用)。 */
export function LevelBadges({
  levels,
  subjects,
  limit = 3,
}: {
  levels?: Record<string, string>;
  subjects?: { id: string; name: string }[];
  limit?: number;
}): JSX.Element | null {
  const entries = Object.entries(levels ?? {});
  if (entries.length === 0) return null;
  const nameOf = (id: string) => subjects?.find((s) => s.id === id)?.name;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {entries.slice(0, limit).map(([sid, lv]) => (
        <LevelBadge key={sid} level={lv} subjectName={nameOf(sid)} />
      ))}
      {entries.length > limit && (
        <span className="text-[10px] text-slate-400">+{entries.length - limit}</span>
      )}
    </span>
  );
}
